import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getInviteByToken, type PublicInvite } from '@/api/invitationsApi';
import MarketingPane from '@/components/MarketingPane';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'invite'; data: PublicInvite }
  | { kind: 'not-found' }
  | { kind: 'error' };

const InvitationLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { loginWithRedirect } = useAuth0();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setView({ kind: 'not-found' }); return; }
    setView({ kind: 'loading' });
    (async () => {
      const r = await getInviteByToken(token);
      if (cancelled) return;
      if (r.success && r.data) setView({ kind: 'invite', data: r.data });
      // A status means the server answered (e.g. 404) — the link is genuinely
      // dead. No status means a network failure, which deserves a retry.
      else if (r.status !== undefined) setView({ kind: 'not-found' });
      else setView({ kind: 'error' });
    })();
    return () => { cancelled = true; };
  }, [token, retryKey]);

  const handleSignup = () => {
    if (view.kind !== 'invite' || !token) return;
    void loginWithRedirect({
      appState: { inviteToken: token },
      authorizationParams: { screen_hint: 'signup', login_hint: view.data.recipient_email },
    });
  };

  const handleLogin = () => {
    void loginWithRedirect({ authorizationParams: { screen_hint: 'login' } });
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[3fr_2fr]">
      <MarketingPane />

      {/* Invitation pane — dark, mirrors Login. */}
      <main className="bg-foreground text-background flex flex-col items-center justify-center p-10 md:p-12">
        <div className="w-full max-w-xs flex flex-col items-center gap-8 text-center">
          {view.kind === 'loading' && (
            <p className="text-sm text-background/60">Loading…</p>
          )}

          {view.kind === 'not-found' && (
            <div className="flex flex-col items-center gap-2">
              <h2 className="text-2xl font-medium tracking-tight">Invitation not found</h2>
              <p className="text-sm text-background/60">
                Check the link or ask your friend to send a new one.
              </p>
            </div>
          )}

          {view.kind === 'error' && (
            <>
              <div className="flex flex-col items-center gap-2">
                <h2 className="text-2xl font-medium tracking-tight">Couldn't load this invitation</h2>
                <p className="text-sm text-background/60">
                  Check your connection and try again.
                </p>
              </div>
              <button
                onClick={() => setRetryKey((k) => k + 1)}
                className="w-full inline-flex items-center justify-center rounded-full bg-background text-foreground h-11 px-6 text-sm font-medium hover:opacity-90"
              >
                Try again
              </button>
            </>
          )}

          {view.kind === 'invite' && view.data.status === 'pending' && (
            <>
              <div className="flex flex-col items-center gap-2">
                <h2 className="text-2xl font-medium tracking-tight">
                  {view.data.inviter_display_name} invited you to Landing
                </h2>
                <p className="text-sm text-background/60">
                  Claim {view.data.discount_summary} when you sign up.
                </p>
              </div>
              <button
                onClick={handleSignup}
                className="w-full inline-flex items-center justify-center rounded-full bg-background text-foreground h-11 px-6 text-sm font-medium hover:opacity-90"
              >
                Sign up as {view.data.recipient_email}
              </button>
            </>
          )}

          {view.kind === 'invite' && view.data.status === 'redeemed' && (
            <>
              <div className="flex flex-col items-center gap-2">
                <h2 className="text-2xl font-medium tracking-tight">This invitation was already used</h2>
                <p className="text-sm text-background/60">If this is your account, log in to continue.</p>
              </div>
              <button
                onClick={handleLogin}
                className="w-full inline-flex items-center justify-center rounded-full bg-background text-foreground h-11 px-6 text-sm font-medium hover:opacity-90"
              >
                Log in
              </button>
            </>
          )}

          {view.kind === 'invite' && view.data.status === 'expired' && (
            <div className="flex flex-col items-center gap-2">
              <h2 className="text-2xl font-medium tracking-tight">This invitation has expired</h2>
              <p className="text-sm text-background/60">
                Invitations are valid for 30 days. Ask {view.data.inviter_display_name} for a new one.
              </p>
            </div>
          )}

          {view.kind === 'invite' && view.data.status === 'revoked' && (
            <div className="flex flex-col items-center gap-2">
              <h2 className="text-2xl font-medium tracking-tight">This invitation is no longer valid</h2>
              <p className="text-sm text-background/60">
                Ask {view.data.inviter_display_name} to send you a new one.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InvitationLanding;
